import { useState } from "react";
import PageLayout from "@/components/feature/PageLayout";
import ScrollReveal from "@/components/base/ScrollReveal";
import { BLOG_HERO_IMAGE } from "@/data/blogAssets";
import { blogPosts } from "@/mocks/blogPosts";

const categories = [
  "All",
  "Getting Started",
  "Anxiety",
  "Relationships",
  "Teen Therapy",
  "Mental Health",
];

export default function BlogAll() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [subStatus, setSubStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [subError, setSubError] = useState("");

  const filteredPosts =
    activeCategory === "All"
      ? blogPosts
      : blogPosts.filter((p) => p.category === activeCategory);

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubStatus("submitting");
    setSubError("");

    const form = e.currentTarget;
    const formData = new FormData(form);
    const email = formData.get("email") as string;

    if (!email || !email.includes("@")) {
      setSubError("Please enter a valid email address.");
      setSubStatus("error");
      return;
    }

    try {
      const res = await fetch("/api/blog-subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (res.ok) {
        setSubStatus("success");
        form.reset();
      } else {
        setSubError("Something went wrong. Please try again.");
        setSubStatus("error");
      }
    } catch {
      setSubError("Something went wrong. Please try again.");
      setSubStatus("error");
    }
  };

  return (
    <>
      <title>
        All Articles | Perspective Therapy Blog Mental Health Larchmont NY
      </title>
      <meta
        name="description"
        content="Browse all articles on mental health, therapy, anxiety, relationships, and teen wellness from Perspective Therapy in Larchmont, NY."
      />
      <PageLayout>
        {/* Hero */}
        <section className="relative w-full min-h-[380px] md:min-h-[460px] flex items-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src={BLOG_HERO_IMAGE}
              alt="Perspective Therapy blog articles"
              className="w-full h-full object-cover object-top"
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(120deg, rgba(28,20,12,0.48) 0%, rgba(42,30,18,0.32) 50%, rgba(60,40,20,0.14) 100%)",
              }}
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(to top, rgba(20,14,8,0.38) 0%, transparent 50%)",
              }}
            />
          </div>
          <div className="relative z-10 w-full px-6 md:px-12 lg:px-20 pt-36 pb-16">
            <div className="max-w-2xl">
              <p className="font-manrope text-white/90 text-[13px] font-semibold tracking-widest uppercase mb-4">
                Insights & Resources
              </p>
              <h1 className="font-fraunces text-white text-3xl md:text-4xl lg:text-[3.2rem] font-light leading-[1.15] mb-5">
                All{" "}
                <em
                  className="not-italic font-normal"
                  style={{ color: "#C9BCEE" }}
                >
                  articles
                </em>
              </h1>
              <p className="font-manrope text-white/98 text-base md:text-lg leading-relaxed max-w-lg">
                Every article on Perspective Therapy's blog, covering mental
                health, therapy, relationships, and personal growth.
              </p>
            </div>
          </div>
        </section>

        {/* Articles */}
        <section
          className="w-full py-16 md:py-24"
          style={{
            background:
              "linear-gradient(180deg, #F8F5EE 0%, #F5F1E8 45%, #F2EDE4 100%)",
          }}
        >
          <div className="w-full px-6 md:px-12 lg:px-20">
            {/* Category filter */}
            <ScrollReveal variant="fadeUp" delay={0}>
              <div className="flex flex-wrap gap-2 mb-10">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className="font-manrope text-[13px] font-semibold px-4 py-2 rounded-full cursor-pointer transition-all whitespace-nowrap"
                    style={{
                      background:
                        cat === activeCategory
                          ? "#3C6FF8"
                          : "rgba(43,43,43,0.06)",
                      color:
                        cat === activeCategory
                          ? "#fff"
                          : "rgba(43,43,43,0.65)",
                    }}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </ScrollReveal>

            {/* Posts list */}
            <div className="flex flex-col gap-5">
              {filteredPosts.map((post, i) => (
                <ScrollReveal
                  key={post.slug}
                  variant="fadeUp"
                  delay={i * 60}
                  threshold={0.04}
                >
                  <a
                    href={`/blog/${post.slug}`}
                    className="bg-white/80 border rounded-2xl overflow-hidden flex flex-col sm:flex-row card-hover block"
                    style={{ borderColor: post.color + "22" }}
                  >
                    <div
                      className="h-[3px] w-full sm:w-[3px] sm:h-auto shrink-0"
                      style={{ background: post.bar }}
                    />
                    <div className="p-6 md:p-7 flex flex-col flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span
                          className="font-manrope text-[11px] font-semibold px-2.5 py-1 rounded-full"
                          style={{
                            background: post.color + "12",
                            color: post.color,
                          }}
                        >
                          {post.category}
                        </span>
                        <span className="font-manrope text-[12px] text-[#2B2B2B]/45">
                          {post.date} &middot; {post.readTime}
                        </span>
                      </div>
                      <h3 className="font-fraunces text-[#2B2B2B] text-lg md:text-xl font-medium mb-2 leading-snug">
                        {post.title}
                      </h3>
                      <p className="font-manrope text-[#2B2B2B]/75 text-[14px] leading-relaxed mb-4">
                        {post.excerpt}
                      </p>
                      <span
                        className="inline-flex items-center gap-1.5 font-manrope text-[13px] font-semibold mt-auto"
                        style={{ color: post.color }}
                      >
                        Read article <i className="ri-arrow-right-line text-xs"></i>
                      </span>
                    </div>
                  </a>
                </ScrollReveal>
              ))}
            </div>

            {filteredPosts.length === 0 && (
              <div className="text-center py-16">
                <p className="font-manrope text-[#2B2B2B]/60 text-[15px]">
                  No articles in this category yet.
                </p>
              </div>
            )}

            {/* External blog link */}
            <ScrollReveal variant="fadeUp" delay={200}>
              <div className="mt-14 flex flex-col sm:flex-row items-center justify-between gap-4 p-6 md:p-8 rounded-2xl border bg-white/80" style={{ borderColor: "rgba(203,191,175,0.30)" }}>
                <div>
                  <h3 className="font-fraunces text-[#2B2B2B] text-lg font-medium mb-1">
                    Looking for more?
                  </h3>
                  <p className="font-manrope text-[#2B2B2B]/75 text-[14px] leading-relaxed">
                    Visit our full blog for additional articles and resources.
                  </p>
                </div>
                <a
                  href="https://www.perspectivetherapyny.com/blog-perspective-therapy-larchmont-new-york"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="inline-flex items-center gap-2 text-white font-manrope font-semibold text-[14px] px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap shrink-0"
                  style={{ background: "#6B742F" }}
                  onMouseEnter={(e) =>
                    ((e.currentTarget as HTMLAnchorElement).style.background =
                      "#5a6328")
                  }
                  onMouseLeave={(e) =>
                    ((e.currentTarget as HTMLAnchorElement).style.background =
                      "#6B742F")
                  }
                >
                  Visit Full Blog <i className="ri-external-link-line"></i>
                </a>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* Subscribe CTA */}
        <section
          className="w-full py-20 md:py-28"
          style={{
            background:
              "linear-gradient(180deg, #EEE8DA 0%, #F5F1E8 45%, #F8F5EE 100%)",
          }}
        >
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="max-w-xl mx-auto text-center">
              <ScrollReveal variant="fadeUp" delay={0}>
                <div className="flex items-center justify-center gap-2 mb-4">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full" style={{ background: "rgba(60,111,248,0.09)" }}>
                    <i className="ri-mail-line text-sm" style={{ color: "#3C6FF8" }}></i>
                  </div>
                </div>
                <h2 className="font-fraunces text-[#2B2B2B] text-3xl md:text-4xl font-light leading-snug mb-4">
                  Want more insights delivered to your inbox?
                </h2>
              </ScrollReveal>
              <ScrollReveal variant="fadeUp" delay={80}>
                <p className="font-manrope text-[#2B2B2B]/78 text-[15px] leading-relaxed mb-8 max-w-md mx-auto">
                  Get blog notifications with practical mental health tips, new
                  articles, and resources from Perspective Therapy.
                </p>
              </ScrollReveal>
              <ScrollReveal variant="fadeUp" delay={160}>
                <form
                  
                  onSubmit={handleSubscribe}
                  className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto"
                >
                  <input
                    type="email"
                    name="email"
                    placeholder="Your email address"
                    required
                    className="flex-1 font-manrope text-[15px] px-5 py-3.5 rounded-full border bg-white/90 focus:outline-none focus:ring-2 focus:ring-[#3C6FF8]/30 text-[#2B2B2B] placeholder:text-[#2B2B2B]/40"
                    style={{ borderColor: "rgba(43,43,43,0.12)" }}
                  />
                  <button
                    type="submit"
                    disabled={subStatus === "submitting"}
                    className="inline-flex items-center justify-center gap-2 text-white font-manrope font-semibold text-[15px] px-7 py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap shrink-0 disabled:opacity-60"
                    style={{ background: "#3C6FF8" }}
                    onMouseEnter={(e) =>
                      ((e.currentTarget as HTMLButtonElement).style.background =
                        "#2B5BE0")
                    }
                    onMouseLeave={(e) =>
                      ((e.currentTarget as HTMLButtonElement).style.background =
                        "#3C6FF8")
                    }
                  >
                    {subStatus === "submitting" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i> Subscribing...
                      </>
                    ) : (
                      <>
                        Subscribe <i className="ri-arrow-right-line"></i>
                      </>
                    )}
                  </button>
                </form>

                {subStatus === "success" && (
                  <p className="mt-4 font-manrope text-[14px] text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-2.5 inline-block">
                    <i className="ri-check-line mr-1"></i> You're subscribed! Check your inbox for a confirmation.
                  </p>
                )}
                {subStatus === "error" && (
                  <p className="mt-4 font-manrope text-[14px] text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 inline-block">
                    <i className="ri-error-warning-line mr-1"></i> {subError}
                  </p>
                )}
              </ScrollReveal>
            </div>
          </div>
        </section>
      </PageLayout>
    </>
  );
}
